import { useCallback, useEffect, useRef } from "react";
import { authFetch } from "@/lib/auth-fetch";
import { apiUrl } from "@/lib/api-base";
import type { Stroke } from "@/components/sketch-canvas";

/**
 * The journal's desktop canvas arrangement as stored on the server
 * (GET/PUT /journals/:id/canvas-layout). Positions are keyed by canvas item id in each
 * item's own layout frame (the same values the page keeps in `cardPos`); drawings are
 * sketch strokes in world px. Viewport (pan/zoom) and tucked-card state are not synced.
 */
export interface CanvasLayout {
  v: 1;
  positions: Record<string, { x: number; y: number }>;
  drawings: Stroke[];
}

const SAVE_DEBOUNCE_MS = 800;
/** Browsers cap keepalive request bodies at 64 KB; larger final flushes go as normal requests. */
const KEEPALIVE_LIMIT = 60_000;
const round = (n: number) => Math.round(n * 10) / 10;

function toPayload(positions: CanvasLayout["positions"], drawings: Stroke[]): CanvasLayout {
  return {
    v: 1,
    positions: Object.fromEntries(Object.entries(positions).map(([k, p]) => [k, { x: round(p.x), y: round(p.y) }])),
    // 0.1px precision is invisible and keeps long drawings compact.
    drawings: drawings.map((s) => ({ ...s, points: s.points.map((p) => ({ x: round(p.x), y: round(p.y) })) })),
  };
}

/**
 * Keeps the desktop canvas layout in sync with the journal on the server, so the same
 * arrangement restores in any browser. localStorage stays as this browser's cache.
 *
 * - Load: once per journal while `enabled` (desktop). A saved server layout wins over the
 *   local cache — unless the user already changed something before it arrived.
 * - Adopt: if the server has no layout yet but this browser has one saved locally (from
 *   before layouts were synced), upload it once so it isn't lost and other browsers get it.
 * - Save: `layoutChanged()` after a real change debounces one PUT of the whole layout;
 *   a pending save is flushed when the page is hidden or the journal is left.
 * - Nothing is written until a load has succeeded, so a failed read (offline, or the
 *   storage not deployed yet) can never overwrite a layout this browser couldn't see.
 */
export function useCanvasLayoutSync({
  journalId,
  enabled,
  positions,
  drawings,
  onServerLayout,
}: {
  journalId: string | undefined;
  enabled: boolean;
  positions: CanvasLayout["positions"];
  drawings: Stroke[];
  onServerLayout: (layout: CanvasLayout) => void;
}): { layoutChanged: () => void } {
  // Latest values for the debounced/flushed save, without re-creating callbacks.
  const latest = useRef({ positions, drawings });
  latest.current = { positions, drawings };
  const onServerLayoutRef = useRef(onServerLayout);
  onServerLayoutRef.current = onServerLayout;

  const state = useRef<{ id?: string; loaded: boolean; dirty: boolean; pending: boolean; storageMissing: boolean }>({
    loaded: false, dirty: false, pending: false, storageMissing: false,
  });
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const save = useCallback((opts?: { keepalive?: boolean }) => {
    const s = state.current;
    clearTimeout(timer.current);
    if (!s.id || !s.loaded || s.storageMissing || !s.pending) return;
    s.pending = false;
    const id = s.id;
    const body = JSON.stringify({ layout: toPayload(latest.current.positions, latest.current.drawings) });
    authFetch(apiUrl(`/journals/${id}/canvas-layout`), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: Boolean(opts?.keepalive) && body.length < KEEPALIVE_LIMIT,
    })
      .then((res) => {
        // 503 = layout storage not deployed yet: stop trying; the local cache still has it.
        if (res.status === 503 && state.current.id === id) state.current.storageMissing = true;
      })
      .catch(() => {
        // Network failure: retry with the next change (the local cache keeps it meanwhile).
        if (state.current.id === id) state.current.pending = true;
      });
  }, []);

  const scheduleSave = useCallback(() => {
    state.current.pending = true;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => save(), SAVE_DEBOUNCE_MS);
  }, [save]);

  const layoutChanged = useCallback(() => {
    if (!enabled) return;
    state.current.dirty = true;
    if (state.current.loaded) scheduleSave();
  }, [enabled, scheduleSave]);

  // Load (once per journal, on desktop).
  useEffect(() => {
    if (!enabled || !journalId) return;
    if (state.current.id === journalId && state.current.loaded) return;
    state.current = { id: journalId, loaded: false, dirty: false, pending: false, storageMissing: false };
    let cancelled = false;
    authFetch(apiUrl(`/journals/${journalId}/canvas-layout`))
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
      .then((data: { layout: CanvasLayout | null }) => {
        if (cancelled || state.current.id !== journalId) return;
        state.current.loaded = true;
        if (data.layout) {
          // Server is the source of truth — unless the user already rearranged something
          // in this session before it arrived, in which case their change is saved instead.
          if (!state.current.dirty) onServerLayoutRef.current(data.layout);
          else scheduleSave();
          return;
        }
        // No server layout yet: adopt this browser's locally saved arrangement, once.
        const { positions: p, drawings: d } = latest.current;
        if (state.current.dirty || Object.keys(p).length > 0 || d.length > 0) scheduleSave();
      })
      .catch(() => {
        // Couldn't read the server layout: keep the local one and don't write anything.
      });
    return () => { cancelled = true; };
  }, [enabled, journalId, scheduleSave]);

  // Flush a pending save when the tab is hidden/closed, and when leaving the journal.
  useEffect(() => {
    const flush = () => save({ keepalive: true });
    const onVisibility = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibility);
      flush();
    };
  }, [save, journalId]);

  return { layoutChanged };
}
